import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Button, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import moment from 'moment-timezone';
import { deleteAudio, deleteAudioAI} from '../../services/apiService';
import ConfirmationButton from '../Popup/ConfirmationButton';


const Container = styled(Box)({
  minHeight: '100vh',
  background: '#2a2d47',
  padding: '40px 20px',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
});

const Card = styled(Box)({
  background: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '16px',
  padding: '32px',
  maxWidth: '800px',
  width: '100%',
});

const Header = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '32px',
});

const DateInfo = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

const DeleteButton = styled(Button)({
  background: '#dc3545',
  color: 'white',
  padding: '8px 20px',
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: 500,
  '&:hover': {
    background: '#c82333',
  },
});

const AudioPlayer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '32px',
});

const PlayButton = styled('button')({
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  background: 'white',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'scale(1.05)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
});

const WaveformContainer = styled(Box)({
  flex: 1,
  height: '48px',
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  position: 'relative',
});

const WaveformBar = styled('div')<{ height: number; active: boolean }>(({ height, active }) => ({
  width: '3px',
  height: `${height}%`,
  background: active ? '#8D49CE' : '#595A70',
  borderRadius: '2px',
  transition: 'all 0.1s ease',
}));

const TimeDisplay = styled(Typography)({
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '14px',
  fontWeight: 400,
  minWidth: '50px',
});

const InputContainer = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
  marginTop: '24px',
});

const StyledTextField = styled(TextField)({
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: '12px',
  },
  '& .MuiOutlinedInput-root': {
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '8px',
    color: 'white',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.1)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
  },
  '& .MuiInputBase-input': {
    padding: '14px',
    fontSize: '14px',
  },
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: 'rgba(255, 255, 255, 0.6)',
  },
});

const VoiceCloneDetails = ({ modalName, getAudioDetails, setDeleteResponse }: any) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);


  const handleDelete = async () => {
    try{
       const response = await deleteAudio(getAudioDetails?._id, 'generated audio', getAudioDetails?.personaModelId, getAudioDetails?.eventId);
    setDeleteResponse(response);
    }catch(error){
      console.error("Error deleting audio:", error);
    }
  };

  const handleDeleteAIModel = async () => {

    try{
       const response = await deleteAudioAI('generated audio', 'ai', modalName?.data?._id);
    setDeleteResponse(response);
    }catch(error){
      console.error("Error deleting AI model:", error);
    }
  }

    const waveformHeights = Array.from({ length: 40 }, () =>
  Math.floor(Math.random() * 60) + 20
);

  // Extract sample audio URL
  const sampleAudioUrl =
    getAudioDetails?.sampleAudioUrl || getAudioDetails?.preview_url || "";

    console.log("sampleAudioUrl", sampleAudioUrl)

  // Play/pause logic for the audio element
  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Listen for audio end to reset play state
  useEffect(() => {
    if (!audioRef.current) return;
    const handleEnded = () => setIsPlaying(false);
    audioRef.current.addEventListener("ended", handleEnded);
    return () => {
      audioRef.current?.removeEventListener("ended", handleEnded);
    };
  }, []);

  // Listen for time update to show progress
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, []);


  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Waveform logic (optional, can sync with audio duration if needed)
  const duration = audioRef.current?.duration || 20;
  const currentProgress = currentTime / duration;

  return (
    <Container>
      <Card sx={{ background: 'transparent', boxShadow: 'none' , border: '1px solid #605869'}}>
        <Header>
          <DateInfo>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', fontWeight: 500 }}
            >
              Last Updated
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px' }}
            >
              {moment(getAudioDetails?.updatedAt).format('MMM DD, YYYY')}
            </Typography>
          </DateInfo>
          <DeleteButton startIcon={<DeleteIcon />}
            // onClick={getAudioDetails?.preview_url ? handleDeleteAIModel : handleDelete}
            onClick={() => setConfirmOpen(true)}
            >
            Delete
          </DeleteButton>
        </Header>

        <AudioPlayer>
          <PlayButton onClick={togglePlay} sx={{ background: 'transparent' }}>
            {isPlaying ? (
              <PauseIcon sx={{ color: '#2a2d47', fontSize: '28px' }} />
            ) : (
              <PlayArrowIcon sx={{ color: '#2a2d47', fontSize: '28px', marginLeft: '3px' }} />
            )}
          </PlayButton>

          {/* Audio element for sample audio */}
          {sampleAudioUrl && (
            <audio
              ref={audioRef}
              src={sampleAudioUrl}
              style={{ display: 'none' }}
            />
          )}

          <WaveformContainer>
            {waveformHeights && waveformHeights.map((height, index) => {
              const barProgress = index / waveformHeights.length;
              return (
                <WaveformBar
                  key={index}
                  height={height}
                  active={barProgress <= currentProgress}
                />
              );
            })}
          </WaveformContainer>
{getAudioDetails?.sampleAudioData?.duration && ( 
  <TimeDisplay>
  {formatTime(
    getAudioDetails?.sampleAudioData?.duration || 0
  )}
</TimeDisplay>) }
         
        </AudioPlayer>

        <InputContainer>
          <StyledTextField
            label="Voice Name"
            value={getAudioDetails?.audioName || getAudioDetails?.name || ''}
            variant="outlined"
            fullWidth
          />
          <StyledTextField
            label="Accent"
            value={getAudioDetails?.labels?.accent || 'English (Indian)'}
            variant="outlined"
            fullWidth
            sx={{textTransform: 'capitalize'}}
            disabled
          />
        </InputContainer>
      </Card>
      {/* Confirmation Dialog for Deletion */}
      {confirmOpen && (
        <ConfirmationButton
          open={confirmOpen}
          handleClose={() => setConfirmOpen(false)}
          handleConfirm={getAudioDetails?.preview_url ? handleDeleteAIModel : handleDelete}
          title="Confirm Deletion"
          Description="Are you sure you want to delete this voice clone? This action cannot be undone."
        />
      )}
    </Container>
  );
};

export default VoiceCloneDetails;